/**
 * 슬라이드 사전 정의 테마 — master-of-slide 의 SKILL.md 들에서 정의한 톤을 가져왔다.
 * 우측 패널의 ThemeGallery 가 이 목록을 카드로 보여주고 클릭하면 slide.design 을 일괄 교체.
 */
import type { SlideDesign } from '@innermate/shared';

export interface SlideTheme {
  id: string;
  name: string;
  description: string;
  /** 카드 미리보기에 보일 작은 hint — 본 캔버스에는 영향 없음. */
  preview: { bg: string; text: string; accent: string };
  design: SlideDesign;
}

const PRETENDARD = 'Pretendard, system-ui, sans-serif';
const SERIF = '"Noto Serif KR", "Source Serif Pro", Georgia, serif';
const MONO = '"JetBrains Mono", "D2Coding", ui-monospace, monospace';

export const SLIDE_THEMES: SlideTheme[] = [
  {
    id: 'paper-press',
    name: '페이퍼 프레스',
    description: '따뜻한 베이지 + 진한 잉크. 강의·리서치·사내 발표 기본.',
    preview: { bg: '#F4EFE6', text: '#1A1A1A', accent: '#B23A48' },
    design: {
      palette: { bg: '#F4EFE6', text: '#1A1A1A', accent: '#B23A48' },
      fonts: { display: PRETENDARD, body: PRETENDARD },
      typeScale: { hero: 160, body: 36 },
      radius: 4,
    },
  },
  {
    id: 'vercel-minimal',
    name: '버셀 미니멀',
    description: '흰 바탕 + 모노 액센트. 기술 발표, 깔끔한 보고.',
    preview: { bg: '#FFFFFF', text: '#0A0A0A', accent: '#0070F3' },
    design: {
      palette: { bg: '#FFFFFF', text: '#0A0A0A', accent: '#0070F3' },
      fonts: { display: PRETENDARD, body: PRETENDARD },
      typeScale: { hero: 144, body: 32 },
      radius: 6,
    },
  },
  {
    id: 'serif-academic',
    name: '세리프 아카데믹',
    description: '아이보리 + 세리프. 논문 발표·정책 보고에 어울림.',
    preview: { bg: '#FAF6EE', text: '#222018', accent: '#7C2A2A' },
    design: {
      palette: { bg: '#FAF6EE', text: '#222018', accent: '#7C2A2A' },
      fonts: { display: SERIF, body: SERIF },
      typeScale: { hero: 156, body: 34 },
      radius: 2,
    },
  },
  {
    id: 'editorial-noir',
    name: '에디토리얼 누아르',
    description: '어두운 배경 + 골드 액센트. 임원 보고·금융 발표.',
    preview: { bg: '#0F0F10', text: '#F5F5F0', accent: '#D4A648' },
    design: {
      palette: { bg: '#0F0F10', text: '#F5F5F0', accent: '#D4A648' },
      fonts: { display: PRETENDARD, body: PRETENDARD },
      typeScale: { hero: 160, body: 36 },
      radius: 8,
    },
  },
  {
    id: 'midnight-blue',
    name: '미드나잇 블루',
    description: '딥 네이비 + 시안. 데이터/분석 보고.',
    preview: { bg: '#0E1B33', text: '#F2F4F8', accent: '#5BC0EB' },
    design: {
      palette: { bg: '#0E1B33', text: '#F2F4F8', accent: '#5BC0EB' },
      fonts: { display: PRETENDARD, body: PRETENDARD },
      typeScale: { hero: 152, body: 34 },
      radius: 8,
    },
  },
  {
    id: 'neo-brutalism',
    name: '네오 브루탈리즘',
    description: '진한 블록색 + 굵은 외곽. 캠페인·마케팅·캠퍼스 발표.',
    preview: { bg: '#FFE600', text: '#0A0A0A', accent: '#FF3B30' },
    design: {
      palette: { bg: '#FFE600', text: '#0A0A0A', accent: '#FF3B30' },
      fonts: { display: PRETENDARD, body: PRETENDARD },
      typeScale: { hero: 184, body: 38 },
      radius: 0,
    },
  },
  {
    id: 'terminal-mono',
    name: '터미널 모노',
    description: '검정 + 형광 그린. 개발자/엔지니어링 회고.',
    preview: { bg: '#0B0B0B', text: '#E6E6E6', accent: '#39FF14' },
    design: {
      palette: { bg: '#0B0B0B', text: '#E6E6E6', accent: '#39FF14' },
      fonts: { display: MONO, body: MONO },
      typeScale: { hero: 144, body: 28 },
      radius: 0,
    },
  },
];

export const DEFAULT_THEME_ID = 'paper-press';

export function getDefaultTheme(): SlideTheme {
  // SLIDE_THEMES 는 항상 비어있지 않음 (정적 배열).
  const fallback = SLIDE_THEMES[0];
  if (!fallback) throw new Error('SLIDE_THEMES is empty');
  return SLIDE_THEMES.find((t) => t.id === DEFAULT_THEME_ID) ?? fallback;
}

/**
 * 주어진 design 이 어떤 사전 정의 테마와 일치하는지 추정.
 * palette 3색 + 폰트 가족 일치를 기준 — 사용자가 토큰을 미세 조정했어도 가장 가까운 테마를 선택 표시.
 */
export function matchTheme(design: SlideDesign): SlideTheme | null {
  for (const t of SLIDE_THEMES) {
    if (
      t.design.palette.bg.toLowerCase() === design.palette.bg.toLowerCase() &&
      t.design.palette.text.toLowerCase() === design.palette.text.toLowerCase() &&
      t.design.palette.accent.toLowerCase() === design.palette.accent.toLowerCase() &&
      t.design.fonts.display === design.fonts.display
    ) {
      return t;
    }
  }
  return null;
}
